import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { MaskLine, EASE } from "./Reveal";
import { scrollToHash } from "./Navbar";

export default function Hero() {
  const ref = useRef(null);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 20 });
  const sy = useSpring(my, { stiffness: 60, damping: 20 });

  const orbX = useTransform(sx, (v) => v * 60);
  const orbY = useTransform(sy, (v) => v * 40);

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const drift = useTransform(scrollYProgress, [0, 1], [0, 120]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      onMouseMove={onMove}
      onMouseLeave={() => {
        mx.set(0);
        my.set(0);
      }}
      className="relative flex min-h-[78vh] items-center overflow-hidden pb-14 pt-28 md:pb-16"
    >
      <div className="hero-grid absolute inset-0" />
      <motion.div
        style={{ x: orbX, y: orbY }}
        className="glow-orb absolute right-[-10%] top-[-15%] h-[60vw] w-[60vw] rounded-full md:right-[5%]"
      />

      <motion.div style={{ opacity: fade, y: drift }} className="relative z-10 mx-auto w-full max-w-[1400px] px-6 md:px-10">
        <div className="flex flex-col justify-between gap-10 lg:flex-row lg:items-end">
          <div className="max-w-[820px]">
            <MaskLine delay={0.1}>
              <span className="mb-6 inline-flex items-center gap-3 font-jbmono text-[11px] uppercase tracking-[0.3em] text-[#00E5FF] md:text-xs">
                <span className="inline-block h-2 w-2 rounded-full bg-[#39FF14]" data-testid="hero-availability-dot" />
                Jacob Tobery — Portfolio ©2026
              </span>
            </MaskLine>

            <h1 className="font-display text-[clamp(2.8rem,8.5vw,6.8rem)] font-black uppercase leading-[0.92] tracking-[-0.02em]">
              <MaskLine delay={0.25}>Jacob</MaskLine>
              <MaskLine delay={0.4}>Tobery</MaskLine>
              <MaskLine delay={0.55}>
                <span className="inline-flex items-center gap-[0.12em]">
                  <span className="text-stroke">Portfolio</span>
                  <motion.span
                    initial={{ scale: 0, rotate: -90 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ delay: 1.1, duration: 0.7, ease: EASE }}
                    className="inline-block h-[0.14em] w-[0.14em] rounded-full bg-[#00E5FF]"
                  />
                </span>
              </MaskLine>
            </h1>

            <MaskLine delay={0.85}>
              <p className="mt-8 max-w-md text-base leading-relaxed text-zinc-400 md:text-lg">
                I&apos;m <span className="text-white">Jacob Tobery</span> — a multifaceted creator
                and Digital Technology &amp; Culture student crafting patterns, posters,
                products and websites, mainly in the Adobe suite. Class of 2027.
              </p>
            </MaskLine>

            <MaskLine delay={0.9}>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <button
                  data-testid="hero-view-work-button"
                  onClick={() => scrollToHash("#work")}
                  className="group rounded-full bg-white px-7 py-3.5 font-jbmono text-xs uppercase tracking-[0.2em] text-black transition-colors duration-300 hover:bg-[#00E5FF]"
                >
                  View work
                  <span className="ml-2 inline-block transition-transform duration-300 group-hover:translate-y-0.5">↓</span>
                </button>
                <button
                  data-testid="hero-contact-button"
                  onClick={() => scrollToHash("#contact")}
                  className="rounded-full border border-white/20 px-7 py-3.5 font-jbmono text-xs uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:border-white/60"
                >
                  Get in touch
                </button>
              </div>
            </MaskLine>
          </div>
        </div>
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 md:block"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          className="font-jbmono text-[10px] uppercase tracking-[0.35em] text-zinc-500"
          data-testid="hero-scroll-cue"
        >
          Scroll
        </motion.div>
      </motion.div>
    </section>
  );
}
